import React, { FC, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useAtomValue } from 'jotai';
import { abyssColors } from '@/theme/colors';
import { unitSystem } from '@/components/Analysis/state';
import { CustomTooltip, renderLegend } from '../GenericBarChart/GenericBarChart';

interface GenericBarChartHorizontalProps {
  title: string;
  data: { name: string; [key: string]: number | string }[];
  legendData: { [key: string]: string };
  showUnit?: boolean;
}

const GenericBarChartHorizontal: FC<GenericBarChartHorizontalProps> = ({
  title,
  data,
  legendData,
  showUnit,
}) => {
  const selectedUnitSystem = useAtomValue(unitSystem);
  const [selectedKeys, setSelectedKeys] = useState(Object.keys(legendData));

  const handleKeyChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const selected = typeof value === 'string' ? value.split(',') : value;

    // Prevents deselecting the last remaining item
    if (selected.length === 0) {
      return;
    }

    setSelectedKeys(selected);
  };

  return (
    <Box
      height={400}
      width="100%"
      padding="16px 24px 50px 24px"
      border={`1px solid ${abyssColors.primary[100]}`}
      borderRadius={4}
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography
          color={abyssColors.primary[500]}
          fontSize={14}
          gutterBottom
          marginBottom={4}
          fontWeight={500}
        >
          {title}
        </Typography>

        <FormControl size="small" style={{ minWidth: 120 }}>
          <Select
            multiple
            value={selectedKeys}
            onChange={handleKeyChange}
            renderValue={
              selectedKeys.length === Object.keys(legendData).length
                ? () => 'All types'
                : (selected) => selected.join(', ')
            }
            sx={{
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: abyssColors.primary[200],
                borderRadius: 2,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: abyssColors.primary[200],
                borderRadius: 2,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: abyssColors.primary[200],
                borderRadius: 2,
              },
              '.MuiSvgIcon-root ': {
                fill: abyssColors.primary[500],
              },
              color: abyssColors.primary[300],
              fontSize: 12,
              height: 32,
            }}
          >
            {Object.keys(legendData).map((key) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={selectedKeys.includes(key)} />
                <ListItemText sx={{ color: abyssColors.primary[300] }} primary={key} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <ResponsiveContainer width="100%" height="95%" id="bar-chart-container">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
          barSize={8}
        >
          <CartesianGrid strokeDasharray="1 1" horizontal={false} vertical={true} />{' '}
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            stroke={`${abyssColors.primary[400]}`}
            tick={{ fontSize: '10px', color: `${abyssColors.primary[400]}` }}
          />
          <YAxis
            dataKey="name"
            type="category"
            width={120}
            axisLine={false}
            tickLine={false}
            stroke={`${abyssColors.primary[400]}`}
            tick={{ fontSize: '12px', color: `${abyssColors.primary[400]}` }}
          />
          <Tooltip
            content={
              <CustomTooltip
                customLabel={title}
                selectedUnitSystem={selectedUnitSystem}
                showUnit={showUnit}
              />
            }
          />
          <Legend content={() => renderLegend(legendData, false)} />
          {legendData &&
            selectedKeys.map((key) => {
              return <Bar key={key} dataKey={key} fill={legendData[key]} />;
            })}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default GenericBarChartHorizontal;
